import {
    autoFocus,
    GroupContext, Label, LabelContext, Modal, TextInput, useValidatedState
} from "acma-spa";
import { debounce } from "acma-spa/dist/pickers/Debounce";
import { reactSelectStyles } from "acma-spa/dist/pickers/PickerStyles";
import { ResponsiveSize, ValidationState } from "acma-spa/dist/Types";
import classNames from "classnames";
import React, { useCallback, useContext, useEffect, useState } from "react";
import AsyncSelect from "react-select/async-creatable";
import { getApiClient, PhoneProvider } from "../api";
import { ClientApiProps } from "../ClientApiProps";
import * as yup from "yup";
import styles from "acma-spa/dist/pickers/PickerStyles.scss";

export interface PhoneProviderPickerProps extends ClientApiProps {
    /**
     * Control id
     */
    id?: string;

    /**
     * Control value
     */
    value: PhoneProvider | null;

    /**
     * Control size
     */
    size?: ResponsiveSize;

    /**
     * Control state
     */
    state?: ValidationState;

    /**
     * Trigger OnChnage
     */
    onChange: (v: PhoneProvider | null) => void;

    /**
     * Trigger disabled
     */
    disabled?: boolean;

    /**
     * Custom class name
     */
    className?: string;
}

interface PhoneProviderOption {
    label: string;
    provider: PhoneProvider | null;
}

interface PhoneProviderDetailsModalProps extends ClientApiProps {
    id: string;
    open: boolean;
    value: PhoneProviderDetailsModalResult;
    onClose: () => any;
    onChange: (v: PhoneProviderDetailsModalResult) => any;
}

interface PhoneProviderDetailsModalResult {
    name: string
}

const PhoneProviderSchema: yup.ObjectSchema<PhoneProviderDetailsModalResult> = yup
    .object()
    .shape({
        name: yup.string().required().label("This"),
    });

const searchByName = async (text: string, props: PhoneProviderPickerProps) => {
    const [client, abort] = getApiClient(props.baseUrl);
    let providers = await client.providers();
    return providers.filter((c) =>
        c?.name?.toLowerCase().startsWith(text.toLowerCase())
    );
};

export const PhoneProviderPicker: React.FC<PhoneProviderPickerProps> = ({
    size = "block",
    ...props
}) => {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState<PhoneProviderOption | null>(() => {
        if (props.value) {
            return {
                label: props.value?.name,
                provider: null,
            };
        } else {
            return null;
        }
    });

    useEffect(() => {
        if (props.value) {
            let label = props.value?.name;
            if (!value || value.label != label) {
                setValue({
                    label: label,
                    provider: null,
                });
            }
        } else if (value) {
            setValue(null);
        }
    }, [props.value]);

    const onSelect = async (v: PhoneProviderOption | null) => {
        setValue(v);
        if (v != null) {
            props.onChange && props.onChange(v.provider);
        } else {
            props.onChange && props.onChange(null);
        }
    };

    const onSet = (v: PhoneProviderDetailsModalResult) => {
        const provider = new PhoneProvider({
            id: "00000000-0000-0000-0000-000000000000",
            name: v?.name
        });

        setValue({
            label: v?.name,
            provider,
        });
        props.onChange && props.onChange(provider);
    };

    const search = async (s: string) => {
        var res: PhoneProviderOption[];
        if (s?.length >= 1) {
            const found = await searchByName(
                s,
                props
            );
            res = found.map((provider) => ({
                label: provider?.name,
                provider,
            }));
        }
        return res;
    };

    const labelCtx = useContext(LabelContext);
    const groupCtx = useContext(GroupContext);
    const state = labelCtx.state || props.state;
    const debouncedSearch = useCallback(debounce(search, 1000), []);

    return (
        <>
            <div className={classNames(styles["au-picker"], {
                [styles[`au-picker--width-${size}`]]: size && size != 'block',
                [styles[`au-picker--block`]]: size == 'block'
            }, groupCtx.className, props.className)}>
            <AsyncSelect<PhoneProviderOption>
                inputId={props.id || labelCtx.id}
                styles={reactSelectStyles(state)}
                className={classNames(groupCtx.className, props.className)}
                placeholder="Start typing a phone provider name..."
                loadOptions={debouncedSearch}
                value={value}
                onChange={onSelect}
                getOptionValue={(r) => r.label}
                isClearable
                isDisabled={props.disabled}
                cacheOptions={false}
                onCreateOption={() => setOpen(true)}
                />
            </div>
            {open && <PhoneProviderDetailsModal
                id={`${props.id || labelCtx.id}-modal`}
                open={true}
                onClose={() => setOpen(false)}
                value={{
                    name: "",
                }}
                onChange={onSet}
                baseUrl={props.baseUrl}
            />}
        </>
    );
};

export const PhoneProviderDetailsModal: React.FC<PhoneProviderDetailsModalProps> = (
    props
) => {

    const [value, setValue, errors, validate] =
        useValidatedState<PhoneProviderDetailsModalResult>(
            { ...props.value },
            PhoneProviderSchema
        );

    const onChange = () => {
        validate(`#${props.id}`).then((res) => {
            if (res) {
                props.onChange(value);
                props.onClose();
            }
        });
    };

    return (
        <Modal
            title="Enter your phone provider"
            id={props.id}
            open={props.open}
            onClose={props.onClose}
            onOpened={() => autoFocus(`#${props.id}`)}
            rightButtons={[
                { label: "OK", onClick: onChange },
                { label: "Close", onClick: props.onClose, mode: "secondary" },
            ]}
        >
            <div className="row">
                <div className="col col-12">
                    <Label
                        label="Your phone provider"
                        id={`${props.id}-name`}
                        error={errors?.name}
                        required
                    >
                        <TextInput
                            size="block"
                            value={value.name}
                            onChange={(v) => setValue({ name: v })}
                            maxLength={100}
                        />
                    </Label>
                </div>
            </div>
        </Modal>
    );
};
